import React, { useRef } from 'react';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux'
import _ from 'lodash'
import { AnimatePresence } from "framer-motion"

import { TextField, ListTile, Button, Icon, Animation } from 'components'
import { FaCheck, FaAngleRight } from 'react-icons/fa'

import { pageContext, sectionContext } from 'context'

const defaultStyle = {
    backgroundColor: 'var(--secondary-color)',
    padding: 0,
    width: '15rem',
    boxShadow: null,
    margin: 0,
    border: '2px solid var(--secondary-color)'
}

const selectedStyle = {
    backgroundColor: 'var(--primary-accent-color)',
    color: 'white',
    border: '2px solid var(--primary-accent-color)',
    padding: '0.2rem 0.2rem',
    margin: '0.1rem'
}

const keyboardSelectedStyle = {
    border: '2px solid var(--focus-color)',
    padding: '0.2rem 0.2rem',
    margin: '0.1rem',
    iconMinWidth: '1rem'
}

const unselectedStyle = {
    border: '2px solid transparent',
    padding: '0.2rem 0.2rem',
    margin: '0.1rem',
    iconMinWidth: '1rem'
}

const iconStyle = {
    backgroundColor: 'transparent',
    margin: 0,
    padding: '0.1rem 0.3rem'
}

const Container = styled.div`
    width: ${props => props.styles.width};
    border-radius: 5px;
    padding: ${props => props.styles.padding};
    margin: ${props => props.styles.margin};
    /* display: flex;
    flex-direction: column; */
    background-color: ${props => props.styles.backgroundColor};    
    box-shadow: ${props => props.styles.boxShadow};
`

const BlockContainer = styled.div`
    overflow-y: auto;
    max-height: 20rem;
    height: min-content;
    padding: 0 0.2rem 0.5rem 0.2rem;
`

const Placeholder = styled.div`
    /* font-weight: bolder; */
    font-size: 0.9rem;
    padding: 0rem 0.5rem 0rem 0.6rem;
    /* text-transform: uppercase; */
    color: var(--secondary-accent-color);
`

const SectionContainer = styled.div`
    margin: 0 0 0 1.75rem;
`

const Section = styled.div`
    /* overflow: hidden; */
    overflow-y: auto;
`

const Title = styled.div`
    font-weight: bolder;
    font-size: 0.7rem;
    padding: 0.5rem 0.5rem 0.5rem 0.6rem;
    text-transform: uppercase;
    color: var(--secondary-accent-color);
    position: sticky;
`

const TextWrapper = styled.div`
    padding: 0.5rem;
    border-bottom: 1px solid var(--secondary-accent-color);
    position: sticky;
`

const SectionSelector = props => {
    const {
        pageID,
        sectionID,
        onSelect,
        styles,
        closeHandler,
        closeOnSelect,
        context,
        singlePage
    } = props
    const updatedStyle = Object.assign({}, defaultStyle, styles)

    // State
    const data = useSelector((state) => state.db.present.data && state.db.present.data.toJS())
    const user = useSelector((state) => state.auth.user)
    const [selectedPage, setPage] = React.useState(pageID)
    const [selectedSection, setSection] = React.useState(sectionID)
    const [pageContext, setPageName] = React.useState(context)

    // Refs
    const containerRef = React.useRef()
    const optionsRef = React.useRef([])

    // Selection state
    const [n, setIndex] = React.useState(-1)

    React.useEffect(() => { // To ensure event listener updates
        const onKeyPress = window.addEventListener('keydown', keyHandler, true)
        return () => {window.removeEventListener('keydown', keyHandler, true)}
    }, [n, selectedPage])

    //////////////
    // HANDLERS //
    //////////////
    const onSelectSection = (pID, secID) => {
        setSection(secID)
        setPage(pID)
        if(closeOnSelect){
            closeHandler && closeHandler()
            onSelect && onSelect(pID, secID)
        }
    }

    const getBlocks = () => {
        let pageIDs = user['activePages']
        // let pageIDs = [user['inboxID']]
        // pageIDs.push(...user['activePages'])
        const search = context || pageContext
        pageIDs = pageIDs.filter(pID => singlePage ? pID === pageID : search ? data['pages'][pID]['page_name'].toLowerCase().includes(search.replace('/','').toLowerCase()) : true)
        let sections = _.filter(data['sections'], {status: sectionContext.STATUS.ACTIVE})
        let blocks = []

        // Generate blocks
        let i = 0
        let ref = []
        let pageRef = []
        Object.values(pageIDs).map((pID, index) => {
            // Page block
            const page = data['pages'][pID]
            if(!page){ return }
            const icon = <Icon value={page.icon ? page.icon.value : null} type={page.icon ? page.icon.type : null} styles={iconStyle}/>
            const defaultSection = _.filter(sections, {section_name: 'Unassigned items', pageID: page['pageID']})[0]
            blocks.push(
                <ListTile icon={icon} text={page['page_name']} 
                    onSelect={()=> {
                        setPage(page['pageID'])
                        setSection(defaultSection['sectionID'])
                    }}
                    styles={selectedPage === page['pageID'] && n === i ? {...selectedStyle, border: '2px solid var(--focus-color)'}
                            : selectedPage === page['pageID'] ? selectedStyle
                            : n === i ? keyboardSelectedStyle
                            : unselectedStyle} />
            )
            ref.push({ value: page['pageID'], type: 'page', pageID: page['pageID'], sectionID: null })
            pageRef.push(page['pageID'])
            i += 1
            // Section blocks
            const showSections = !search ? selectedPage === page['pageID']
                                : search && pageIDs.includes(selectedPage) ? selectedPage === page['pageID']
                                : search && !pageIDs.includes(selectedPage) ? index === 0
                                : selectedPage === page['pageID']
            
            const selectedIcon = <Icon defaultIcon={<FaCheck/>} 
                                    styles={{color: 'var(--focus-color)',cursor: 'pointer', backgroundColor: 'transparent', padding: 0}}/>
            Object.values(page['sections']).map(secID => {
                const section = data['sections'][secID]
                if(section && section['status'] === sectionContext.STATUS.ACTIVE){
                    ref.push({ value: secID, type: 'section', pageID: page['pageID'], sectionID: secID })
                    blocks.push(
                    <SectionContainer>
                        <ListTile text={section['section_name']} icon={<FaAngleRight/>}
                            onSelect={()=> {onSelectSection(page['pageID'], secID)}} 
                            trailingIcon={selectedSection === secID && selectedIcon}
                            styles={n === i ? {...keyboardSelectedStyle, padding: '0.2rem'} : {...unselectedStyle, padding: '0.2rem'}}
                            trailingPersist clickable/>
                    </SectionContainer>)
                    i += 1
                }
            })
        })

        // Placeholder if got nothing
        blocks.length === 0 && blocks.push(<Placeholder>No active pages</Placeholder>)
        return { blocks, ref, pages: pageRef }
    }

    const { blocks, ref, pages } = getBlocks()
    
    ///////////////////////
    // KEYBOARD HANDLERS //
    ///////////////////////
    const keyHandler = (event) => {
        let i
        const max = blocks.length - 2  
        switch(event.keyCode){
            case 40: // Down
                i = n <= max ? n + 1 
                    : n > max ? 0
                    : n
                setIndex(i)
                scrollHandler('down',i)
                break
            case 38: // Up
                i = n > 0 ? n - 1 
                    : n <= 0 ? max + 1
                    : n
                setIndex(i)
                scrollHandler('up',i)
                break
            case 13: // Enter
                const selected = ref[n]
                if((selected['type'] === 'page')){
                    // setPage(selected['value'])
                    // setIndex(pages.indexOf(selected['value']) + 1)
                } else if (selected['type'] === 'section'){
                    setPage(selected['pageID'])
                    setSection(selected['sectionID'])
                    setTimeout(() => onSelectSection(selected['pageID'], selected['sectionID']), 100)
                }
                break
            case 27: // Esc
                break
            case 8: // Backspace
                break
        }
    }

    const scrollHandler = (direction, index) => {
        if(!containerRef.current) { return }

        const containerRect = containerRef.current.getBoundingClientRect()
        const bottoms = ref.map((block, i) => optionsRef.current[i] && optionsRef.current[i].getBoundingClientRect()['bottom'] - containerRect['y'])
        const tops = ref.map((block, i) => optionsRef.current[i] && optionsRef.current[i].getBoundingClientRect()['top'] - containerRect['y'])

        let scrollTo = -1
        if(index === ref.length){
            scrollTo = containerRect['height'] + 1000
        } else if (index === 0) {
            scrollTo = 0
        } else {
            scrollTo = bottoms[index] > containerRect['height'] ? tops[index] + containerRef.current.scrollTop
                    // : tops[index] < 0 ? Math.abs(bottoms[index]) + Math.abs(tops[index])
                    : tops[index] < 0 ? containerRef.current.scrollTop + tops[index]
                    : -1
        } 
        scrollTo > -1 && containerRef.current.scrollTo({left: 0, top: scrollTo, behavior: 'smooth'})
    }

    return (
        <Container styles={updatedStyle}>
            {!context && 
            <TextWrapper>
                <TextField type='single' focus onChange={(event) => setPageName(event.target.value)} 
                    value={pageContext} placeholder='Select page'
                    styles={{fontSize:'0.9rem'}}/>
            </TextWrapper>}
            <Title>Select page and section</Title>

            <BlockContainer ref={containerRef}>
                {blocks.map((block,index) => (<div ref={el => optionsRef.current[index] = el}>{block}</div>))}
            </BlockContainer>

            <div style={{display: 'flex', justifyContent: 'flex-end'}}>
                {!closeOnSelect &&
                <Button text='Apply' onSelect={() => onSelect(selectedPage, selectedSection)} 
                    styles={{
                        width: 'min-content',
                        backgroundColor:'var(--focus-color)',
                        margin:'0.2rem 0',
                        fontWeight: 'bolder',
                        color: 'white'
                    }}/>}
            </div>
        </Container>
    )
}

export default SectionSelector